import QuizInteractiveFlow from "@/components/storyboard/quiz/QuizInteractiveFlow";
import QuizGapwordFreeText, {
  type QuizGapwordFreeTextState,
} from "@/components/storyboard/quiz/QuizGapwordFreeText";
import { useValidationSubmissions } from "@/hooks/useValidationSubmissions";
import { resolveClozeFreetextStatus } from "@/lib/freetextQuizStatus";
import { InteractiveElementStatusKind } from "@/lib/interactiveElementState";
import type { QuizGapwordFreeTextEntryData } from "@/types/storyboard";
import type { ValidationResult } from "@/services/QuizValidationService";

export interface QuizGapwordFreeTextFlowProps {
  id: string;
  title: string;
  entries: QuizGapwordFreeTextEntryData[];
  onValidate: (
    answers: string[],
    entryIndex: number
  ) => Promise<ValidationResult>;
}

/** Cloze (free-text gaps) quiz embedded in the shared interactive-element flow.
 * The answers and verdicts live on the server, so a reload — or another device
 * — meets the quiz in the state the learner left it.
 */
const QuizGapwordFreeTextFlow = ({
  id,
  title,
  entries,
  onValidate,
}: QuizGapwordFreeTextFlowProps) => {
  const { submissions, refresh, isReady } = useValidationSubmissions(id);
  const statusOverride = resolveClozeFreetextStatus(submissions, entries);
  const serverResume =
    statusOverride?.kind === InteractiveElementStatusKind.InProgress
      ? (statusOverride.partial as QuizGapwordFreeTextState)
      : undefined;

  return (
    <QuizInteractiveFlow<QuizGapwordFreeTextState>
      id={id}
      title={title}
      illustrationKind="cloze_freetext"
      layout="inline"
      statusOverride={statusOverride}
      supportsInProgress
      reportProgress
      renderQuiz={({
        initialPartial,
        initialIndex,
        onPartialChange,
        onComplete,
      }) => {
        if (initialPartial == null && !isReady) return null;

        return (
          <QuizGapwordFreeText
            entries={entries}
            onValidate={async (answers, entryIndex) => {
              const result = await onValidate(answers, entryIndex);
              refresh();
              return result;
            }}
            initialPartial={initialPartial ?? serverResume}
            initialIndex={initialIndex ?? serverResume?.currentIndex}
            onPartialChange={onPartialChange}
            onComplete={onComplete}
          />
        );
      }}
    />
  );
};

export default QuizGapwordFreeTextFlow;
